/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  ScrollView,
  Dimensions,
} from 'react-native';
import SociollaText from '@components/sociolla/SociollaText';
import Modal from '@components/modal/Modal';
import Image from '@components/global/Image';
import Colors from '@global/colors';
import { isWeb, isMobileWeb, isNative } from '@global/screens';
import { Portal, PortalHost } from '@gorhom/portal';
import { parseDate } from '@global/helpers';
import { MONTH_NAME } from '@/constants';

const dimension = Dimensions.get('window');

const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const toMidnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

const isSameDay = (a, b) => {
  if (!a || !b) {
    return false;
  }
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const buildYears = () => {
  const years = [];
  let y = 2012;
  const end = new Date().getFullYear() + 10;
  while (y <= end) {
    years.push(y++);
  }
  return years;
};
const YEARS = buildYears();

const buildGrid = (fulldate) => {
  const dates = [[], [], [], [], [], [], []];
  let index = 0;
  const startOfMonth = new Date(fulldate.getFullYear(), fulldate.getMonth(), 1);
  const endOfMonth = new Date(
    fulldate.getFullYear(),
    fulldate.getMonth() + 1,
    0,
  );
  for (let i = 0; i < startOfMonth.getDay(); i++) {
    dates[0].push('');
  }
  for (let i = 0; i < endOfMonth.getDate(); i++) {
    const date = new Date(fulldate.getFullYear(), fulldate.getMonth(), i + 1);
    dates[index].push(date);
    if (dates[index].length >= 7) {
      index++;
    }
  }
  return dates;
};

const SociollaDateRangePicker = (props) => {
  const { visible, onClose, onChange, startValue, endValue } = props;

  const [pendingStart, setPendingStart] = useState(null);
  const [pendingEnd, setPendingEnd] = useState(null);
  const [fulldate, setFullDate] = useState(new Date());
  const [showYearList, setShowYearList] = useState(false);
  const [lists, setLists] = useState([[], [], [], [], [], [], []]);
  const [dateHeight, setDateHeight] = useState(30);
  const [dateHeightChanged, setDateHeightChanged] = useState(false);

  useEffect(() => {
    if (visible) {
      const s = startValue ? new Date(startValue) : null;
      const e = endValue ? new Date(endValue) : null;
      setPendingStart(s);
      setPendingEnd(e);
      setFullDate(s || new Date());
      setShowYearList(false);
    }
  }, [visible]);

  useEffect(() => {
    setLists(buildGrid(fulldate));
  }, [fulldate]);

  const handleDateSelect = (date) => {
    if (!date) {
      return;
    }
    const d = toMidnight(date);
    if (!pendingStart || pendingEnd) {
      setPendingStart(d);
      setPendingEnd(null);
    } else {
      if (d < pendingStart) {
        setPendingStart(d);
        setPendingEnd(null);
      } else {
        setPendingEnd(d);
      }
    }
  };

  const isStartDate = (date) =>
    date && pendingStart ? isSameDay(date, pendingStart) : false;
  const isEndDate = (date) =>
    date && pendingEnd ? isSameDay(date, pendingEnd) : false;

  const isInRange = (date) => {
    if (!date || !pendingStart || !pendingEnd) {
      return false;
    }
    const d = toMidnight(date);
    return d > pendingStart && d < pendingEnd;
  };

  const headerText = () => {
    if (!pendingStart) {
      return 'Select start date';
    }
    if (!pendingEnd) {
      return `${parseDate(pendingStart, 'd MMM yyyy')} → Select end date`;
    }
    return `${parseDate(pendingStart, 'd MMM yyyy')} — ${parseDate(
      pendingEnd,
      'd MMM yyyy',
    )}`;
  };

  const handleConfirm = () => {
    if (!pendingStart) {
      return;
    }
    onChange({ start: pendingStart, end: pendingEnd || pendingStart });
  };

  const onLayoutItem = (e) => {
    try {
      setTimeout(() => {
        if (!dateHeightChanged && !isNative) {
          setDateHeightChanged(true);
          setDateHeight(e.nativeEvent.target.clientWidth);
        }
      }, 500);
    } catch (_err) {}
  };

  const arrowSize = { width: 8, height: 12 };

  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="slide"
      spaceFromTop={'10%'}
      maxWidth={isWeb ? 280 : isMobileWeb ? 'calc(100% - 30px)' : 260}
      onClose={onClose}>
      <View style={styles.datepicker}>
        <View style={styles.datepickerTop}>
          <View>
            <TouchableOpacity
              activeOpacity={0.6}
              style={styles.yearContainer}
              onPress={() => setShowYearList(true)}>
              <SociollaText type="bold" style={styles.yearText}>
                {fulldate.getFullYear()}
              </SociollaText>
            </TouchableOpacity>
            {showYearList ? (
              <Portal hostName="portal_year_range">
                <View style={styles.yearList}>
                  <ScrollView>
                    {YEARS.map((yr, idx) => (
                      <TouchableOpacity
                        key={idx}
                        activeOpacity={0.6}
                        style={styles.yearItem}
                        onPress={() => {
                          setFullDate(new Date(yr, fulldate.getMonth(), 1));
                          setShowYearList(false);
                        }}>
                        <SociollaText type="bold" style={{ fontSize: 14 }}>
                          {yr}
                        </SociollaText>
                      </TouchableOpacity>
                    ))}
                  </ScrollView>
                </View>
              </Portal>
            ) : null}
          </View>
          <SociollaText
            type="medium"
            style={styles.rangeText}
            numberOfLines={1}>
            {headerText()}
          </SociollaText>
        </View>

        <View style={styles.monthRow}>
          <TouchableOpacity
            style={styles.arrowBtn}
            activeOpacity={0.6}
            onPress={() =>
              setFullDate(
                new Date(fulldate.getFullYear(), fulldate.getMonth() - 1, 1),
              )
            }>
            <Image
              style={arrowSize}
              width={arrowSize.width}
              height={arrowSize.height}
              svg={true}
              source={require('@assets/img/icons/arrow-left-grey.svg')}
            />
          </TouchableOpacity>
          <SociollaText style={{ fontSize: 12, color: Colors.black }}>
            {MONTH_NAME[fulldate.getMonth()]} {fulldate.getFullYear()}
          </SociollaText>
          <TouchableOpacity
            style={styles.arrowBtn}
            activeOpacity={0.6}
            onPress={() =>
              setFullDate(
                new Date(fulldate.getFullYear(), fulldate.getMonth() + 1, 1),
              )
            }>
            <Image
              style={arrowSize}
              width={arrowSize.width}
              height={arrowSize.height}
              svg={true}
              source={require('@assets/img/icons/arrow-right-grey.svg')}
            />
          </TouchableOpacity>
        </View>

        <View style={styles.daysRow}>
          {dayLetters.map((d, idx) => (
            <View key={idx} style={[styles.dateCell, styles.rowCenter]}>
              <SociollaText
                type="bold"
                style={{ fontSize: 12, color: Colors.grey1 }}>
                {d}
              </SociollaText>
            </View>
          ))}
        </View>

        <View style={styles.datesGrid}>
          {lists.map((row, rowIdx) =>
            row.map((date, colIdx) => {
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              const inRange = isInRange(date);
              return (
                <TouchableOpacity
                  key={`${rowIdx}-${colIdx}`}
                  activeOpacity={0.6}
                  style={[
                    styles.dateCell,
                    styles.rowCenter,
                    !isNative ? { height: dateHeight } : {},
                    { borderRadius: 60 },
                    isStart || isEnd ? { backgroundColor: Colors.blue0 } : {},
                    inRange
                      ? { backgroundColor: Colors.blue12, borderRadius: 0 }
                      : {},
                  ]}
                  onPress={() => handleDateSelect(date)}
                  onLayout={onLayoutItem}
                  disabled={!date}>
                  <SociollaText
                    type={isStart || isEnd ? 'bold' : 'regular'}
                    style={{
                      fontSize: 12,
                      color:
                        isStart || isEnd
                          ? Colors.white
                          : inRange
                          ? Colors.blue0
                          : {},
                    }}>
                    {date ? date.getDate() : ''}
                  </SociollaText>
                </TouchableOpacity>
              );
            }),
          )}
        </View>

        <View style={styles.btnRow}>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.btn}
            onPress={onClose}>
            <SociollaText
              type="bold"
              style={[styles.btnText, { color: Colors.blue1 }]}>
              CANCEL
            </SociollaText>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.6}
            style={styles.btn}
            onPress={handleConfirm}
            disabled={!pendingStart}>
            <SociollaText
              type="bold"
              style={[
                styles.btnText,
                { color: pendingStart ? Colors.blue0 : Colors.grey1 },
              ]}>
              SELECT
            </SociollaText>
          </TouchableOpacity>
        </View>
        <PortalHost name="portal_year_range" />
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  datepicker: {
    width: isNative ? dimension.width - 70 : '100%',
    borderRadius: 5,
    overflow: 'hidden',
    backgroundColor: Colors.white,
    marginLeft: isNative ? 35 : 'auto',
    marginTop: isNative ? '20%' : 0,
  },
  datepickerTop: {
    width: '100%',
    alignItems: 'flex-start',
    backgroundColor: Colors.black,
    paddingHorizontal: 15,
    paddingTop: 20,
    paddingBottom: 15,
  },
  yearContainer: {
    marginBottom: 5,
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 2,
    backgroundColor: Colors.black6,
  },
  yearText: {
    fontSize: 14,
    color: Colors.grey0,
    lineHeight: 20,
  },
  rangeText: {
    fontSize: 15,
    color: Colors.white,
  },
  yearList: {
    position: 'absolute',
    maxHeight: 150,
    left: 15,
    top: 25,
    backgroundColor: Colors.white,
    zIndex: 9,
    elevation: 99,
    borderWidth: 1,
    borderColor: Colors.grey0,
    borderRadius: 4,
  },
  yearItem: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey0,
  },
  monthRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: isNative ? 25 : 15,
    paddingTop: 15,
    paddingBottom: 10,
  },
  arrowBtn: {
    width: 20,
    minWidth: 20,
    height: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  daysRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
    paddingTop: 10,
  },
  datesGrid: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  dateCell: {
    width: isNative ? (dimension.width - 105) / 7 : '14.28%',
    minWidth: isNative ? (dimension.width - 105) / 7 : '14.28%',
    height: isNative ? (dimension.width - 105) / 7 : 30,
  },
  rowCenter: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: 10,
  },
  btn: {
    paddingHorizontal: 15,
    paddingVertical: 15,
  },
  btnText: {
    fontSize: 13,
    letterSpacing: 0.5,
  },
});

export default React.memo(
  SociollaDateRangePicker,
  (prev, next) => prev === next,
);
